import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import ConfirmDialog from '../../utils/ConfirmDialog';
import logger from '../../utils/logger';

const UserRow = ({ user, roles, modifiedUsers, onRoleChange, setModifiedUsers, variant = 'row' }) => {
  const isModified = modifiedUsers[user.id] !== undefined;
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, 'users', user.id), { role: modifiedUsers[user.id] });
      setModifiedUsers((prev) => {
        const updated = { ...prev };
        delete updated[user.id];
        return updated;
      });
    } catch (error) {
      logger.error('Error updating role:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'users', user.id));
    } catch (error) {
      logger.error('Error deleting user:', error);
    }
  };

  if (variant === 'card') {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-card">
        <ConfirmDialog
          open={showConfirm}
          title="Delete User"
          message={`Are you sure you want to delete "${user.email}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => { handleDelete(); setShowConfirm(false); }}
          onCancel={() => setShowConfirm(false)}
        />
        <div className="text-sm font-medium text-gray-900 break-words">{user.email}</div>
        <div className="grid grid-cols-2 gap-1.5 mt-2 text-xs text-gray-500">
          <div><span className="text-gray-400">Name:</span> {user.full_name || 'N/A'}</div>
          <div><span className="text-gray-400">Institution:</span> {user.institution || 'N/A'}</div>
          <div><span className="text-gray-400">Age:</span> {user.age || 'N/A'}</div>
          <div><span className="text-gray-400">Requested:</span> {user.requested_role || 'N/A'}</div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-50">
          <label className="text-xs text-gray-400 block mb-1">Current Role</label>
          <select
            value={modifiedUsers[user.id] || user.role || 'user'}
            onChange={(e) => onRoleChange(user.id, e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
          >
            {roles.map((role, idx) => (
              <option key={idx} value={role}>{role}</option>
            ))}
          </select>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap gap-1.5">
          <button
            className={`px-2.5 py-1 text-xs font-medium rounded-md text-white transition-colors ${
              isModified ? 'bg-brand-600 hover:bg-brand-700' : 'bg-brand-300 cursor-not-allowed'
            }`}
            disabled={!isModified}
            onClick={handleSave}
          >
            Save
          </button>
          <button
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
            onClick={() => setShowConfirm(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog
        open={showConfirm}
        title="Delete User"
        message={`Are you sure you want to delete "${user.email}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => { handleDelete(); setShowConfirm(false); }}
        onCancel={() => setShowConfirm(false)}
      />
      <tr className="hover:bg-gray-50/70 transition-colors">
      <td className="px-4 py-3 text-gray-700 text-xs break-words" data-label="Email">{user.email}</td>
      <td className="px-4 py-3 text-gray-700 text-xs break-words" data-label="Full Name">{user.full_name || 'N/A'}</td>
      <td className="px-4 py-3 text-gray-500 text-xs break-words" data-label="Institution">{user.institution || 'N/A'}</td>
      <td className="px-4 py-3 text-gray-500 text-xs text-center" data-label="Age">{user.age || 'N/A'}</td>
      <td className="px-4 py-3" data-label="Role">
        <select
          value={modifiedUsers[user.id] || user.role || 'user'}
          onChange={(e) => onRoleChange(user.id, e.target.value)}
          className="w-full max-w-[160px] px-2 py-1 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
        >
          {roles.map((role, idx) => (
            <option key={idx} value={role}>{role}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3 text-gray-500 text-xs break-words" data-label="Requested Role">{user.requested_role || 'N/A'}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          <button
            className={`px-2.5 py-1 text-xs font-medium rounded-md text-white transition-colors ${
              isModified ? 'bg-brand-600 hover:bg-brand-700' : 'bg-brand-300 cursor-not-allowed'
            }`}
            disabled={!isModified}
            onClick={handleSave}
          >
            Save
          </button>
          <button
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
            onClick={() => setShowConfirm(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            Delete
          </button>
        </div>
      </td>
    </tr>
    </>
  );
};

export default UserRow;
